import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { MatLegacyCheckboxChange as MatCheckboxChange } from '@angular/material/legacy-checkbox';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { LegacyPageEvent as PageEvent } from '@angular/material/legacy-paginator';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { Router } from '@angular/router';
import {
  AgGridEvent,
  CellClickedEvent,
  ColDef,
  ColumnApi,
  ColumnMovedEvent,
  ColumnState,
  GridApi,
  GridOptions,
  GridReadyEvent,
  RowNode,
  RowSelectedEvent,
} from 'ag-grid-community';
import { pick } from 'lodash';
import moment from 'moment';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { debounceTime, finalize, take, takeUntil } from 'rxjs/operators';

import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { ColType } from 'app/shared/utils/ag-grid.col-def.utils';
import { LumAgGridEvent } from 'app/shared/utils/ag-grid.menu-item.utils';
import {
  EApprovalStatus,
  EApprovalStatusValue,
} from 'v2/modules/approval/interfaces/approval.interface';
import { EAssemblyOrderStatus } from 'v2/modules/assembly-order/interfaces';
import {
  CustomFieldFormComponent,
  CustomFieldFormGroupComponent,
} from 'v2/modules/custom-field/components';
import { ECustomFieldObject, ICustomField } from 'v2/modules/custom-field/interfaces/custom-field.interface';
import { OrderPostedTagsFormComponent } from 'v2/modules/order/components/order-posted-tags-form/order-posted-tags-form.component';
import {
  IOrderSalesPicklistPreview,
  IOrderSalesPicklistPreviewItem,
  IOrderSalesShipstationAddress,
} from 'v2/modules/order/interfaces/order-picklist-preview';
import {
  ISalesOrder,
  IOrderPickingPreviewDialogInterface,
  EOrderTaggableType,
  IOrderCreateItem,
} from 'v2/modules/order/interfaces/order.interface';
import { PicklistPreviewDialogComponent } from 'v2/modules/picklist/components/picklist-preview-dialog/picklist-preview-dialog.component';
import { PicklistConfig } from 'v2/modules/picklist/config/picklist.config';
import { IAgGridEditorDateParams } from 'v2/modules/shared/components/ag-grid-editor-date/ag-grid-editor-date.component';
import {
  DialogConfig,
  PaginationConfig,
  SnackBarConfig,
} from 'v2/modules/shared/configs';
import { AgGridConstants } from 'v2/modules/shared/constants/ag-grid.constants';
import {
  EModelType,
  IAgGridFilter,
  IAppIntegrationAccount,
  IChannel,
  IIdName,
  IPagination,
  IValueLabel,
  IWarehouseGroup,
  LocalStorageKeys,
} from 'v2/modules/shared/interfaces';
import { IAgGridFilterModelValue } from 'v2/modules/shared/interfaces/ag-grid/ag-grid.types';
import {
  IOrderActions,
  IOrderAgTableLineItemsPageEvent,
  IOrderData,
  IOrderRowData,
  EOrderType,
} from 'v2/modules/shared/interfaces/order.interface';
import { ITagOld } from 'v2/modules/shared/interfaces/tag.interface';
import { CustomFieldParseValuePipe } from 'v2/modules/shared/pipes/custom-field-parse-value.pipe';
import { AppIntegrationAccountApiService } from 'v2/modules/shared/services/api/app-integration/app-integration-account-api.service';
import { ApprovalApiService } from 'v2/modules/shared/services/api/approval/approval-api.service';
import { CustomFieldApiService } from 'v2/modules/shared/services/api/custom-field-api.service';
import { OrderApiService } from 'v2/modules/shared/services/api/order/order-api.service';
import { OrderPostedApiService } from 'v2/modules/shared/services/api/order/order-posted-api.service';
import { OrderSalesApiService } from 'v2/modules/shared/services/api/order/order-sales-api.service';
import { PicklistApiService } from 'v2/modules/shared/services/api/picklist-api.service';
import { SalesOrderApiService } from 'v2/modules/shared/services/api/sales-order-api.service';
import { SalesOrderFulfillmentFacadeService } from 'v2/modules/shared/services/facades/sales-order/sales-order-fulfillment.facade.service';
import { AgGridPaginationUtilService } from 'v2/modules/shared/services/util/ag-grid-pagination-util.service';
import { FeatureFlagUtilService } from 'v2/modules/shared/services/util/feature-flag-util.service';
import { LoadingDialogUtilService } from 'v2/modules/shared/services/util/loading-dialog-util.service';
import { AgGridUtils } from 'v2/modules/shared/util/ag-grid.utils';
import { exportB64toPdf } from 'v2/modules/shared/util/export.utils';
import { trackById, trackByLabel } from 'v2/modules/shared/util/track-by.util';
import { TagsAutocompleteChipDialogComponent } from 'v2/modules/tag/components/tags-autocomplete-chip-dialog/tags-autocomplete-chip-dialog.component';
import { ITag } from 'v2/modules/tag/interfaces/tag.interface';
import { UiConfirmationDialogComponent } from 'v2/modules/ui/components/ui-confirmation-dialog/ui-confirmation-dialog.component';
import { IEventDumbFilterRemove } from 'v2/standalone/dumb-filters/dumb-filters.types';

import { toCapitalize } from '../../../shared/util/string-transformation.utils';
import { SalesOrderAgTableConfig, SalesOrderConfig } from '../../configs';
import {
  buildSalesOrderAgGridColDefs,
  SALES_ORDER_AG_GRID_DETAIL_COL_DEFS,
} from '../../configs/sales-order-ag-table-col-defs';
import {
  ESalesOrderStatusValue,
  ISalesOrderGroup,
} from '../../interfaces/sales-order.interface';
import { SalesOrderActionsHelperService } from '../../services/sales-order-actions-helper.service';
import { SalesOrderViewSettingsService } from '../../services/sales-order-view-settings.service';
import {
  ISalesOrderConfirmationDialogData,
  SalesOrderConfirmationDialogComponent,
} from '../sales-order-confirmation-dialog/sales-order-confirmation-dialog.component';
import { SalesOrderCustomFieldCellEditorComponent } from '../sales-order-custom-field-cell-editor/sales-order-custom-field-cell-editor.component';
import { CreateInvoiceFromSalesOrderFormComponent } from '../sales-order-invoicing';
import { SalesOrderLineItemsAssemblyOrderDialogComponent } from '../sales-order-line-items-assembly-order-dialog/sales-order-line-items-assembly-order-dialog.component';
import { SalesOrderLineItemsTransferOrderDialogComponent } from '../sales-order-line-items-transfer-order-dialog/sales-order-line-items-transfer-order-dialog.component';

import { TagApiService } from './../../../shared/services/api/tag-api.service';
import {
  SalesOrderLineItemsPurchaseOrderDialogComponent,
  PurchaseOrderOptions,
} from './../sales-order-line-items-purchase-order-dialog/sales-order-line-items-purchase-order-dialog.component';

//

interface _ITogglePreferenceOptions {
  updateUserPreference ?:boolean;
}
interface _IToggleSidebarOptions extends _ITogglePreferenceOptions {}
interface _IToggleFiltersOptions extends _ITogglePreferenceOptions {}

let _id = 0;
const _DUMMY_FILTERS = [
  {
    attribute: 'Status',
    predicate: 'is one of',
    values: ['Pending', 'Complete'],
  },
  { attribute: 'Channel', predicate: 'is empty' },
  { attribute: 'Order #', predicate: 'starts with', values: ['LUM-'] },
  { attribute: 'Age', predicate: '>', values: ['5 days'] },
  {
    attribute: 'Requested Ship Date',
    predicate: 'is between',
    values: ['Jan 1', 'Feb 1'],
  },
  { attribute: 'Order ID', predicate: 'is NOT empty' },
];
const newDummyFilter = () =>
  _DUMMY_FILTERS[_id++] ??
  _DUMMY_FILTERS[Math.round((_DUMMY_FILTERS.length - 1) * Math.random())];

enum _ETab {
  ORDERS = 'ORDERS',
  LINE_ITEMS = 'LINE_ITEMS',
}

//

@Component({
  selector: 'lum-sales-order-ag-table',
  templateUrl: './sales-order-ag-table.component.html',
  styleUrls: ['./sales-order-ag-table.component.scss'],
})
export class SalesOrderAgTableComponent implements OnInit, OnDestroy, OnChanges {
  @Input()
  orderStatus :string | null;

  @Input()
  ftPickAndPackEnabled :boolean;

  @Input()
  ftRemoveWoocommerceFromChannelType :boolean;

  @Input()
  ftEdiIntegrationEnabled :boolean;

  @Input()
  ftEdi810InvoiceSyncEnabled :boolean;

  @Input()
  canMarkAsShipped :boolean;

  @Input()
  canDeleteSalesOrder :boolean;

  @Input()
  channels :IChannel[] = [];

  @Input()
  warehouseGroups :IWarehouseGroup[] = [];

  @Input()
  inventoryPickAndPackEnabled :boolean = false;

  @Input()
  childComponentConfigs :any;

  @Input()
  latestOrderStatusUpdate :string;

  @Input()
  latestHoldOrderUpdate :string;

  @Input()
  latestPrintShippingLabelUpdate :string;

  @Input()
  pickingItemsLatestEntry :string;

  @Input()
  latestSalesOrderUpdate :string;

  @Input()
  ftApplyPresets :boolean = false;

  @Input()
  currentCustomFilter :IAgGridFilter | null;

  @Input()
  channelsHasPushOrderMapping :boolean;

  @Input()
  tags :ITagOld[] = [];

  @Output()
  previewPicklistItemsEvent :EventEmitter<ISalesOrder[]> = new EventEmitter();

  @Output()
  createTrackingNumberEvent = new EventEmitter<{
    salesOrderId :number;
    trackingNumbers :string[];
  }>();

  @Output()
  setToPendingEvent = new EventEmitter<number>();

  @Output()
  holdOrderEvent = new EventEmitter<{
    id :number;
    orderNumber :string;
    source :string;
    shipstationOrderId ?:any;
  }>();

  @Output()
  printShippingLabelEvent = new EventEmitter<any>();

  @Output()
  quickFulfillmentFlowEvent = new EventEmitter<string>();

  @Output()
  adjustmentEntryEvent = new EventEmitter<number>();

  @Output()
  returnSelectedRowsEvent = new EventEmitter<ISalesOrder[]>();

  @Output()
  isOnSyncJobs :EventEmitter<boolean> = new EventEmitter();

  @Output()
  appIntegrationAccountEvent :EventEmitter<number | null> = new EventEmitter();

  @Output()
  onClickOrderNumberEvent = new EventEmitter<number>();

  @Input()
  authUserId :number;

  @Input()
  ftApprovalModuleEnabled :boolean;

  @Output()
  agGridStateChangedEvent = new EventEmitter<void>(); // You can add any return value here if needed to be passed to parent component

  private shipstationShipments :any[] = [];

  readonly _ETab = _ETab;
  readonly trackByLabel = trackByLabel;
  readonly trackById = trackById;

  activeTab :_ETab = _ETab.ORDERS;
  rowData :ISalesOrder[] = [];
  loading$ :BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  loadingLineItems$ :BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  picklistStatus = PicklistConfig.picklistStatus;
  columns = [...SalesOrderAgTableConfig.columns];
  relations = [...SalesOrderAgTableConfig.relations];
  onDemandServerSideRelations = SalesOrderAgTableConfig.onDemandServerSideRelations;
  onDemandServerSideColumns = SalesOrderAgTableConfig.onDemandServerSideColumns;

  gridOptions :GridOptions = SalesOrderAgTableConfig.gridOptions;
  fields = SalesOrderAgTableConfig.fields;

  defaultColDef :ColDef = SalesOrderAgTableConfig.defaultColDef;
  pageSizeOptions = SalesOrderAgTableConfig.pageSizeOptions;
  paginationPageSize :number = 100;
  paginationCurrentPage :number = 0;
  currentQueryParams = SalesOrderAgTableConfig.defaultQueryParams;
  isInitialized :boolean = false;
  paginationConfig = PaginationConfig;
  pagination :IPagination = {
    current_page: 1,
    from: null,
    last_page: 1,
    per_page: 100,
    to: null,
    total: 0,
    pageIndex: null,
  };
  groupBy :string = '';
  groupByOptions = SalesOrderAgTableConfig.groupByColumns;
  groups :ISalesOrderGroup[] = [];

  selectedRows :ISalesOrder[] = [];
  selectAllPerformed :boolean = false;

  selectedRowNode :RowNode | null = null;
  selectedColor :string = '';

  sideNavShippingCollapse :boolean = false;
  sideNavItemsCollapse :boolean = false;

  isSelectAllRows :boolean = false;
  currentOnDemandRelation :string | null = null;
  totalRowCount :number = 0;
  isSidebarHiddenBySelectAll :boolean = false;
  appIntegrationAccounts :IAppIntegrationAccount[] = [];
  orderItemActions :IOrderActions[] = [
    {
      label: 'Create Assembly Order',
      onRowClick: (data :any) => this.onToggleAssemblyOrderDialog(data),
    },
    {
      label: 'Create Purchase Order',
      onRowClick: (data :any) => this.onCreatePurchaseOrder(data),
    },
    {
      label: 'Create Transfer Order',
      onRowClick: (data :any) => this.onToggleTransferOrderDialog(data),
    },
    {
      label: 'Assign Tags',
      onRowClick: (data :any) => this.onToggleAssignTagsDialog(data),
      visible: (_ :any[], childSelectedRows :any[]) => {
        return !childSelectedRows?.length ? true : false;
      },
    },
  ];
  private columnVisibilityChangeSubject = new Subject<void>();

  columnDefs :ColDef[] = buildSalesOrderAgGridColDefs({
    actionsCellParams: {
      applyBackgroundColor: (data :{ id :number; backgroundColor :string }) =>
        this.applyBackgroundColor(data),
      openCreatePicklistModal: (id :number) => this.redirectAndCreatePicklist(id),
      editSalesOrder: (id :number) => this.editSalesOrder(id),
      deleteSalesOrder: (id :number) => this.deleteSalesOrder(id),
      updateCustomFields: (data :ISalesOrder) => this.updateCustomFields(data),
      flagOrder: (id :number) => this.flagOrder(id),
      unFlagOrder: (id :number) => this.unFlagOrder(id),
      approveOrder: (data :ISalesOrder) => this.approveOrder(data),
      rejectOrder: (data :ISalesOrder) => this.rejectOrder(data),
      setToPending: (id :number) => this.setToPending(id),
      holdOrder: (data :{
        id :number;
        orderNumber :string;
        source :string;
        shipstationOrderId ?:any;
      }) => this.holdOrder(data),
      updateTags: (id :number) => this.updateTags(id),
      onSendEdiTransactionDocument: (
        salesOrderId :number,
        ediTransactionNumber :string,
      ) => this.sendEdiTransactionDocument(salesOrderId, ediTransactionNumber),
      openCreateTrackingNumber: (data :{
        salesOrderId :number;
        trackingNumbers :string[];
      }) => this.openCreateTrackingNumber(data),
      printShippingLabel: (data :any) => this.openPrintShippingLabel(data),
      createSalesOrderInvoice: (shipstationOrderId :number) =>
        this.createSalesOrderInvoice(shipstationOrderId),
      pushFulfillment: (id :number) => this.pushFulfillment(id),
      onUnpostOrder: (id :number) => this.onUnpostOrder(id),
    },
    getSourceValues: () => this.getSourceValues(),
    handleOrderNumberCellClick: (params :any, field :string) => {
      this.onClickCellRendererEvent(params, field);
    },
    saveShipByDateCell: this.featureFlagUtilService.isFeatureEnabled(
      'utah_FR-577_make_sales_order_grid_requested_ship_date_cell_editable',
    )
      ? (params :IAgGridEditorDateParams, value :string) :Observable<any> => {
          const data = {
            [params.column.getColId() as string]: value
              ? moment(value).endOf('day').toISOString()
              : '',
            isRequestedShipByDateUpdate: 'true',
          };

          return this.orderApiService.updateRowColumn(params.data.id, data);
        }
      : null,
  });

  loadingConsolidatedItems :boolean = false;

  pickingItems :IOrderSalesPicklistPreview;
  consolidatedOrderItems :IOrderSalesPicklistPreviewItem[] = [];
  warehouseAddress :IOrderSalesShipstationAddress | null = null;
  selectedAppIntegrationAccount :number | null = null;

  /** DO NOT set this directly; use `#toggleSidebar()` instead. */
  isSidebarOpen :boolean = this._viewSettings.get('prefersSidebarOpen');
  /** DO NOT set this directly; use `#toggleFilters()` instead. */
  isFiltersOpen :boolean = this._viewSettings.get('prefersFiltersOpen');

  activeFilters = [newDummyFilter(), newDummyFilter()];
  lineItemRowData :IOrderRowData;
  linteItemsQueryParams :{ [key :string] :string | string[] } = {
    sortDirection: 'desc',
    sortField: 'created_at',
    limit: '100',
  };
  eOrderType = EOrderType;

  private rowSelectionChanged$ = new Subject<void>();

  private onDestroy$ = new Subject();
  private gridApi! :GridApi;
  private gridColumnApi :ColumnApi;
  private lineItemDetailGridApi! :GridApi;
  private lineItemGridApi! :GridApi;

  constructor(
    private _actions :SalesOrderActionsHelperService,
    private _feature :FeatureToggleService,
    private _viewSettings :SalesOrderViewSettingsService,
    private orderApiService :OrderApiService,
    private cd :ChangeDetectorRef,
    private dialog :MatDialog,
    private customFieldApiService :CustomFieldApiService,
    private customFieldParserPipe :CustomFieldParseValuePipe,
    private snackBar :MatSnackBar,
    private router :Router,
    private agGridPaginationUtilService :AgGridPaginationUtilService,
    private picklistApiService :PicklistApiService,
    private loadingDialogService :LoadingDialogUtilService,
    private orderPostedApiService :OrderPostedApiService,
    private orderSalesApiService :OrderSalesApiService,
    private snackbar :MatSnackBar,
    private approvalApiService :ApprovalApiService,
    private appIntegrationAccountApiService :AppIntegrationAccountApiService,
    private featureFlagUtilService :FeatureFlagUtilService,
    private salesOrderFulfillmentFacadeService :SalesOrderFulfillmentFacadeService,
    private salesOrderApiservice :SalesOrderApiService,
    private tagApiService :TagApiService,
  ) {}

  ngOnInit() :void {
    this.initMasterRows();
    this.initColumnDefs();
    this.initOnDemandParams();
    this.initGroupState();
    this.initOverrideColumns();
    this.initRowSelection();
    this.#connectGridContext();
    this.#loadOrderLineItems();

    if (!this._feature.isActive('utah-UTAH-356-so_grid_advanced_filters')) {
      this.columnDefs.map((col) => {
        if ([SalesOrderAgTableConfig.fields.SKU].includes(col.field)) {
          col.filterParams.filterOptions = ['contains'];
        }

        return col;
      });
    }
  }

  ngOnChanges({
    orderStatus,
    channels,
    latestTrackingNumber,
    latestOrderStatusUpdate,
    latestHoldOrderUpdate,
    latestPrintShippingLabelUpdate,
    pickingItemsLatestEntry,
    latestSalesOrderUpdate,
    currentCustomFilter,
    tags,
  } :SimpleChanges) :void {
    if (
      (orderStatus?.currentValue || orderStatus?.currentValue === null) &&
      !currentCustomFilter?.currentValue
    ) {
      // this.updateOrderStatusFilter(orderStatus?.currentValue);
      if (this.groupBy) {
        this.groups = [];
        this.fetchGroupedSalesOrders();
      }
    }
    if (channels?.currentValue?.length) {
      this.handleChannelFilterUpdate();
    }

    if (tags?.currentValue?.length) {
      this.#handleTagFilterUpdate();
    }

    let shouldRefetchTableData = [
      latestTrackingNumber,
      latestOrderStatusUpdate,
      latestHoldOrderUpdate,
      latestPrintShippingLabelUpdate,
      latestSalesOrderUpdate,
    ].some((change) => !!change?.currentValue);

    if (
      orderStatus?.currentValue ||
      (orderStatus?.currentValue &&
        this.storedPageState?.activeCustomFilters?.orderStatuses &&
        this.storedPageState?.activeCustomFilters?.orderStatuses !==
          orderStatus?.previousValue)
    ) {
      // This method is now called only when the order status is changed.
      this.updateOrderStatusFilter(
        orderStatus?.currentValue ||
          this.storedPageState?.activeCustomFilters?.orderStatuses,
      );

      // If orderStatus previous value is not the same as the this.storedPageState?.activeCustomFilters?.orderStatuses, reset sorting
      if (
        orderStatus?.previousValue &&
        orderStatus?.previousValue !==
          this.storedPageState?.activeCustomFilters?.orderStatuses
      ) {
        this.gridOptions.columnApi?.applyColumnState({
          defaultState: { sort: null },
        });
      }

      if (
        orderStatus &&
        orderStatus?.previousValue &&
        orderStatus?.currentValue !== orderStatus?.previousValue
      ) {
        shouldRefetchTableData = true;
      }
    }

    if (pickingItemsLatestEntry?.currentValue) {
      this.getConsolidatedOrderItems();
    }

    if (currentCustomFilter?.currentValue && !orderStatus?.currentValue) {
      const state = currentCustomFilter?.currentValue;
      localStorage.setItem(
        LocalStorageKeys.SALES_ORDER_AG_STATE,
        JSON.stringify(state),
      );

      if (state?.filterModel && Object.keys(state.filterModel)?.length) {
        this.gridApi?.setFilterModel(state.filterModel);
        this._viewSettings.updateState({ agFilterModel: state.filterModel });
      }

      if (
        state?.columnState &&
        Object.keys(state.columnState)?.length &&
        this.gridColumnApi
      ) {
        this.gridColumnApi.applyColumnState({
          state: state.columnState,
          applyOrder: true,
        });
      }
    }

    if (shouldRefetchTableData) {
      this.onRefetchTableData();
    }
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  initRowSelection() :void {
    this.rowSelectionChanged$
      .pipe(debounceTime(100), takeUntil(this.onDestroy$))
      .subscribe(() => {
        const displayedRowCount = this.gridApi?.getDisplayedRowCount();
        const selectedAllVisible =
          this.gridApi?.getSelectedNodes().length === displayedRowCount;

        if (
          // if they've filtered to just 1 or 2 rows, we want to keep all
          // actions available, so we only "select all rows" if there are 5 or
          // more and they're all selected [calvin]
          this.totalRowCount > 4 &&
          this.totalRowCount === displayedRowCount
        ) {
          // all rows are visible
          if (selectedAllVisible) {
            this.#toggleSelectAllRows(true);
          }
        }

        if (!this.isSelectAllRows) {
          this.getConsolidatedOrderItems();

          if (this._viewSettings.get('prefersSidebarOpen') && !this.isSidebarOpen) {
            this.#openSidebar();
          }
        }
      });
  }

  handleFilterAdd() {
    this.activeFilters.push(newDummyFilter());
  }
  handleFilterRemove({ index } :IEventDumbFilterRemove) {
    this.activeFilters.splice(index, 1);
  }
  handleFiltersClear() {
    this.activeFilters = [];
  }

  handleShowFiltersClick(e :PointerEvent) {
    this.#toggleFilters(null, { updateUserPreference: true });
  }
  #toggleFilters(toState ?:boolean, options ?:_IToggleFiltersOptions) {
    toState ??= !this.isFiltersOpen;

    // exit early if the state is already correct
    if (this.isFiltersOpen === toState) {
      return;
    }

    // do it
    this.isFiltersOpen = toState;

    this.cd.detectChanges();

    // update local storage
    if (options.updateUserPreference) {
      this._viewSettings.updateState({
        prefersFiltersOpen: this.isFiltersOpen,
      });
    }
  }
  #closeFilters() {
    this.#toggleFilters(false);
  }
  #openFilters() {
    this.#toggleFilters(true);
  }

  handleShowSidebarChange(e :MatCheckboxChange) {
    this.#toggleSidebar(e.checked, { updateUserPreference: true });
  }
  #toggleSidebar(toState ?:boolean, options ?:_IToggleSidebarOptions) {
    toState ??= !this.isSidebarOpen;
    options = {
      updateUserPreference: false,
      ...options,
    };

    // exit early if the state is already correct
    if (this.isSidebarOpen === toState) {
      return;
    }

    // do it
    this.isSidebarOpen = toState;

    this.cd.detectChanges();

    // update local storage
    if (options.updateUserPreference) {
      this._viewSettings.updateState({
        prefersSidebarOpen: this.isSidebarOpen,
      });
    }
  }
  #closeSidebar(options ?:_IToggleSidebarOptions) {
    this.#toggleSidebar(false, options);
  }
  #openSidebar(options ?:_IToggleSidebarOptions) {
    this.#toggleSidebar(true, options);
  }

  onFilterChanged() :void {
    this.selectedRows = [];
    this.gridApi?.deselectAll();
    this._viewSettings.updateState({ agFilterModel: this.currentFilterModel });
  }

  isRowSelectable(node :any) :boolean {
    return node.data?.isPosted;
  }

  onRowSelected(params :RowSelectedEvent) :void {
    this.processOnRowSplice(params);

    const index = this.selectedRows.findIndex(
      (order) => order.id === params.data.id,
    );

    if (params.node.isSelected()) {
      if (index === -1) {
        this.selectedRows = [...this.selectedRows, params.data];
      }
    } else {
      if (index !== -1) {
        this.selectedRows.splice(index, 1);
      }
    }

    this.onChangeSelectedRowSingleRow();

    // when this happens because of clicking the checkbox instead of the cell,
    // so we need to handle the range selection. [calvin]
    if (
      params.source === 'checkboxSelected' &&
      params.event && // no event means its a simulated select
      !params.context.selectionRange.isSelecting()
    ) {
      params.context.selectionRange.reset(params.rowIndex);
    }
  }

  onChangeSelectedRowSingleRow() :void {
    this.shipstationShipments = [];
    if (this.selectedRows.length === 1) {
      this.loadShipstationShipments(this.firstSelectedRow?.id);
    }
  }

  getSourceValues() :string[] {
    let sources = SalesOrderConfig.sources;

    if (this.ftRemoveWoocommerceFromChannelType) {
      sources = sources.filter(
        (source) => source.id !== SalesOrderConfig.woocommerceSourceConstant.id,
      );
    }

    return sources.map((source) => source.id);
  }

  handleUpdateSelectedRows(selectedRows :ISalesOrder[]) :void {
    if (!this.gridApi) {
      return;
    }

    this.gridApi.forEachNode((rowNode) => {
      const isSelected = selectedRows.some(({ id }) => id === rowNode.data.id);

      rowNode.setSelected(isSelected);
    });

    this.selectedRows = this.gridApi.getSelectedNodes().map((node) => node.data);
  }

  onGridReady(params :GridReadyEvent<any>) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;

    if (this.warehouseGroups?.length) {
      this.handleWarehouseGroupFilterUpdate();
    }

    this.handleMenuUpdate();

    const filterModel = {
      ...this.storedPageState?.filterModel,
      ...this._viewSettings.get('agFilterModel'),
    };

    if (Object.keys(filterModel)?.length) {
      this.gridApi?.setFilterModel(filterModel);
    }

    if (
      this.storedPageState?.columnState &&
      Object.keys(this.storedPageState.columnState)?.length
    ) {
      this.gridColumnApi.applyColumnState({
        state: this.storedPageState.columnState,
        applyOrder: true,
      });
    }

    if (this.groupBy) {
      this.onGroupByChanged(this.groupBy);
    } else {
      this.onRefetchTableData();
    }

    this.attachEventListenersOnPageStateChange();
    this.loadCustomFields();
    this.getAppIntegrationAccounts();
    this.#listenForCustomGridEvents();
  }

  handleMenuUpdate() :void {
    const actionColumn = this.columnDefs.find(
      (col) => col.field === this.fields.ACTIONS,
    );
    if (actionColumn) {
      actionColumn.cellRendererParams.ftPickAndPackEnabled =
        this.ftPickAndPackEnabled;
      actionColumn.cellRendererParams.canDeleteSalesOrder =
        this.canDeleteSalesOrder;
      actionColumn.cellRendererParams.ftEdiIntegrationEnabled =
        this.ftEdiIntegrationEnabled;
      actionColumn.cellRendererParams.ftApprovalModuleEnabled =
        this.ftApprovalModuleEnabled;
      actionColumn.cellRendererParams.authUserId = this.authUserId;
    }

    this.gridApi?.refreshCells();
    this.gridApi?.setColumnDefs(this.columnDefs);
    this.cd.detectChanges();
  }

  onPaginationEvent(pagination :PageEvent) :void {
    this.currentQueryParams = {
      ...this.currentQueryParams,
      page: pagination.pageIndex + 1,
      limit: pagination.pageSize,
    };

    localStorage.setItem(
      LocalStorageKeys.SALES_ORDER_TABLE_PAGINATION,
      JSON.stringify(pagination),
    );

    if (this.groupBy) {
      if (this.currentGroupsTotalCount < this.expectedRecordsCount) {
        this.fetchGroupedSalesOrders();
      } else {
        this.loadSalesOrders();
        this.loadAllSelectableRowCount();
      }
    } else {
      this.loadSalesOrders();
      this.loadAllSelectableRowCount();
    }
  }

  onResetFilters() :void {
    this.currentQueryParams = SalesOrderAgTableConfig.defaultQueryParams;
    // Reset local storage
    localStorage.removeItem(LocalStorageKeys.SALES_ORDER_AG_STATE);

    this.gridOptions.api?.setFilterModel(null);
    this.gridOptions.columnApi?.applyColumnState({
      defaultState: { sort: null },
    });
  }

  setRowGroupStatus(data :{ columnField :string; status :boolean }) :void {
    const { columnField, status } = data;
    // Get the existing column definitions
    const columnDefs = this.gridApi?.getColumnDefs() as ColDef[];

    // Find and update the relevant column
    for (const colDef of columnDefs) {
      if (colDef.field === columnField) {
        colDef.rowGroup = status;
        break;
      }
    }

    // Update the column definitions in the grid
    this.gridOptions.api?.setColumnDefs(columnDefs);
  }

  onGroupByChanged(value :string) :void {
    if (value) {
      if (
        Object.keys(this.onDemandServerSideRelations).includes(value) &&
        !this.relations.includes(value)
      ) {
        this.relations.push(this.onDemandServerSideRelations[value]);
      }
      this.setRowGroupStatus({
        columnField: value,
        status: true,
      });
      if (
        this.groupBy !== value ||
        this.pagination.current_page * this.pagination.per_page >=
          this.currentGroupsTotalCount
      ) {
        this.removeColumnByField(this.groupBy);
        this.groupBy = value;
        this.setCurrentQueryParams();
        this.fetchGroupedSalesOrders();
      }
      this.groupBy = value;
    } else {
      this.removeColumnByField(this.groupBy);
      this.setRowGroupStatus({
        columnField: this.groupBy,
        status: false,
      });
      this.groupBy = value;
      this.onRefetchTableData();
    }
    localStorage.setItem(LocalStorageKeys.SALES_ORDER_AG_GROUP_STATE, value);
  }

  getRowStyle(params :any) :any {
    const backgroundColor = params.data?.backgroundColor;

    return backgroundColor ? { background: backgroundColor } : null;
  }

  onPreviewPicklistItems(salesOrders :ISalesOrder[]) :void {
    this.previewPicklistItemsEvent.emit(salesOrders);
    this.appIntegrationAccountEvent.emit(this.selectedAppIntegrationAccount);
  }

  createCustomField() :void {
    this.dialog
      .open(CustomFieldFormComponent, {
        width: DialogConfig.sizes.large,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.setCustomFieldCells([result]);
        }
      });
  }

  onRefetchTableData() :void {
    this.selectedRows = [];
    this.gridApi?.deselectAll();

    this.setCurrentQueryParams();
    this.loadSalesOrders();
    this.loadAllSelectableRowCount();
  }

  onResetTable() :void {
    localStorage.removeItem(LocalStorageKeys.SALES_ORDER_AG_STATE);

    if (this.gridApi) {
      this.gridApi.setFilterModel(null);

      if (this.gridOptions.columnApi) {
        this.gridOptions.columnApi.applyColumnState({
          state: [],
          applyOrder: true,
        });

        this.gridOptions.columnApi.resetColumnState();
      }

      this.gridApi.refreshCells({ force: true });
    }
  }

  onRowSelectionChanged() :void {
    this.rowSelectionChanged$.next();
  }

  onSyncJobs(isOnSyncJob :boolean) :void {
    this.isOnSyncJobs.emit(isOnSyncJob);
  }

  printShippingLabel() :void {
    if (this.firstSelectedRow && this.shipstationShipments?.length) {
      const shipmentsWithLabelData =
        this.firstSelectedRow.shipstationShipments.filter(
          (shipment :any) => shipment.labelData,
        );

      if (shipmentsWithLabelData.length === 0) {
        return;
      }

      const filename = this.firstSelectedRow.id + '-shipping-label';
      const shipment = shipmentsWithLabelData[0];

      exportB64toPdf(shipment.labelData, filename);
    }
  }

  initMasterRows() :void {
    this.gridOptions.masterDetail = true;
    this.gridOptions.embedFullWidthRows = true;
    this.gridOptions.isRowMaster = (data :ISalesOrder) => {
      return data?.isRowMaster;
    };

    this.gridOptions.detailCellRendererParams = {
      template: AgGridConstants.DEFAULT_DETAIL_CELL_RENDERER_TEMPLATE,
      getDetailRowData: (params :any) => {
        this.picklistApiService
          .getPickingItemsBySalesOrderId(params.data.id)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe({
            next: (response) => {
              params.successCallback(response.pickingItems);
            },
            error: (response) => {
              this.snackBar.open('Unable to load picking items.', 'Fail', {
                duration: SnackBarConfig.DEFAULT.duration,
              });
            },
          });
      },
      detailGridOptions: {
        columnDefs: SALES_ORDER_AG_GRID_DETAIL_COL_DEFS,
      },
    };

    this.cd.detectChanges();
  }

  onUpdateRecipientForm(event :boolean) :void {
    if (event) {
      this.setCurrentQueryParams();
      this.loadSalesOrders();
      this.loadAllSelectableRowCount();
    }
  }

  gotoPage(pageValue :string) :void {
    if (pageValue === '') {
      return;
    }
    const pageValueInput = Number(pageValue);
    const pageEvent = new PageEvent();
    pageEvent.pageIndex = pageValueInput - 1; // because pageIndex is 0-based
    pageEvent.pageSize = this.pagination.per_page;
    pageEvent.length = this.pagination.total;
    this.onPaginationEvent(pageEvent);
  }

  handleGoToPageInput(inputElement :HTMLInputElement) :void {
    const inputElementValue :number = Number(inputElement.value);
    if (inputElementValue === 0) {
      inputElement.value = '1';
    }
    if (inputElementValue > this.pagination.last_page) {
      inputElement.value = this.pagination.last_page.toString();
    }
  }

  onInitQuickFulfillmentEvent(event :string) :void {
    this.returnSelectedRowsEvent.emit(this.selectedRows);
    this.quickFulfillmentFlowEvent.emit(event);
  }

  onCreateAdjustmentEntry(event :number) :void {
    this.returnSelectedRowsEvent.emit(this.selectedRows);
    this.adjustmentEntryEvent.emit(event);
  }

  onNoteSelectAllClick(event :Event) :void {
    event.preventDefault();

    this.#toggleSelectAllRows(true);
  }

  onNoteClearSelectionClick(event :Event) :void {
    event.preventDefault();

    this.#toggleSelectAllRows(false);
  }

  onBulkUpdateSuccess(event :boolean) :void {
    if (event) {
      this.getConsolidatedOrderItems();
    }
  }
  onCellClicked(params :CellClickedEvent) {
    const shiftKeyPressed = (params.event as PointerEvent).shiftKey;

    if (shiftKeyPressed) {
      this.#selectRange(params);
    } else {
      this.#selectRow(params);
    }
  }
  #selectRow(params :CellClickedEvent) {
    params.context.selectionRange.reset(params.rowIndex);

    // clicking anywhere in the checkbox cell behaves as if you hit the checkbox
    // which should save loads of near-miss issues [calvin]
    if (params.colDef?.colId === ColType.CHECKBOX) {
      // toggle selected state as if the checkbox was clicked
      params.node.setSelected(
        !params.node.isSelected(),
        false,
        false,
        'checkboxSelected',
      );
    } else {
      params.node.setSelected(true, true);
    }
  }
  #selectRange(params :CellClickedEvent) {
    const { selectionRange } = params.context;

    if (!selectionRange.isSelecting()) {
      this.#selectRow(params);

      return;
    }

    const currentStart = Math.min(selectionRange.start, params.rowIndex);
    const currentEnd = Math.max(selectionRange.start, params.rowIndex);

    if (selectionRange.isComplete()) {
      const start = selectionRange.min;
      const end = selectionRange.max;

      for (let i = start; i <= end; i++) {
        if (i < currentStart || i > currentEnd) {
          params.api.getRowNode(`${i}`).setSelected(false);
        }
      }
    }

    for (let i = currentStart; i <= currentEnd; i++) {
      params.api.getRowNode(`${i}`).setSelected(true);
    }

    selectionRange.end = params.rowIndex;
  }

  onColumnMoved(event :ColumnMovedEvent) {
    if (event.finished) {
      const state =
        JSON.parse(
          localStorage.getItem(LocalStorageKeys.SALES_ORDER_AG_STATE) || '{}',
        ) || {};

      this._viewSettings.updateState({
        agFilterModel: {
          ...this.currentFilterModel,
          ...state.filterModel,
        },
      });
      localStorage.setItem(
        LocalStorageKeys.SALES_ORDER_AG_STATE,
        JSON.stringify({
          filterModel: this.currentFilterModel,
          columnState: this.currentColumnState,
          activeCustomFilters: this.currentActiveCustomFilters,
        }),
      );
    }
  }

  onToggleFilterSideBar() :void {
    const openedToolPanel = this.gridOptions.api?.getOpenedToolPanel();
    if (openedToolPanel === 'filters') {
      this.gridApi?.closeToolPanel();
    } else {
      this.gridApi?.openToolPanel('filters');
    }
  }

  onToggleAssemblyOrderDialog(data :{
    parentSelectedRows :any[];
    childSelectedRows :any[];
  }) :void {
    const { parentSelectedRows = [], childSelectedRows = [] } = data;
    const orderItems :IOrderCreateItem[] = [
      ...parentSelectedRows.map((item) => ({
        itemId: item.id,
        orderItemId: item.orderItemId,
        orderId: item.orderId,
        itemType: 'sales_order_item',
      })),
      ...childSelectedRows.map((item) => ({
        itemId: item.id,
        orderItemId: item.orderItemId,
        orderId: item.orderId,
        itemType: 'bom_item',
      })),
    ];
    this.dialog
      .open(SalesOrderLineItemsAssemblyOrderDialogComponent, {
        width: DialogConfig.Size['X_SMALL'],
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe({
        next: (orderStatus :EAssemblyOrderStatus) => {
          if (orderStatus) {
            this.snackBar.open('Creating assembly order...', 'Dismiss');
            this.orderApiService
              .createFromItems({
                sourceOrderType: EOrderType.SALES_ORDER,
                destinationOrderType: 'assembly_order',
                orderItems,
                orderStatus,
              })
              .pipe(takeUntil(this.onDestroy$))
              .subscribe({
                next: (response) => {
                  this.snackBar.open(
                    response?.success || 'Assembly order created successfully',
                    'Dismiss',
                    {
                      duration: SnackBarConfig.DEFAULT.duration,
                    },
                  );
                  this.router.navigate(['/assembly-orders'], {
                    queryParams: { status: orderStatus },
                  }); // Add redirect here
                },
                error: (err) => {
                  this.snackBar.open(
                    err?.error || 'Error creating assembly order',
                    'Dismiss',
                    {
                      duration: SnackBarConfig.ERROR.duration,
                    },
                  );
                },
              });
          }
        },
      });
  }

  onCreatePurchaseOrder(data :{
    parentSelectedRows :any[];
    childSelectedRows :any[];
  }) :void {
    const { parentSelectedRows = [], childSelectedRows = [] } = data;

    // Get unique orderNumbers using a simple object
    const uniqueOrders = {};
    parentSelectedRows.forEach((row) => {
      uniqueOrders[row.orderId] = row.orderNumber;
    });

    // Convert to array of orderNumbers
    const alternateIds = Object.values(uniqueOrders);

    this.dialog
      .open(SalesOrderLineItemsPurchaseOrderDialogComponent, {
        width: DialogConfig.Size.SMALL,
        data: {
          alternateIds,
        },
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe({
        next: (options :PurchaseOrderOptions) => {
          if (!options) return; // User canceled

          const orderItems :IOrderCreateItem[] = [
            ...parentSelectedRows.map((item) => ({
              itemId: item.id,
              orderItemId: item.orderItemId,
              orderId: item.orderId,
              itemType: 'sales_order_item',
            })),
            ...childSelectedRows.map((item) => ({
              itemId: item.id,
              orderItemId: item.orderItemId,
              orderId: item.orderId,
              itemType: 'bom_item',
            })),
          ];

          this.snackBar.open('Creating purchase order...', 'Dismiss');
          this.orderApiService
            .createFromItems({
              sourceOrderType: EOrderType.SALES_ORDER,
              destinationOrderType: 'supplier_purchase_order',
              orderItems,
              combineIdenticalSkus: options.combineIdenticalSkus, // Pass the option to the backend
              alternateIds: options.alternateIds,
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response) => {
                this.snackBar.open(
                  response?.success || 'Purchase order created successfully',
                  'Dismiss',
                  {
                    duration: SnackBarConfig.DEFAULT.duration,
                  },
                );
              },
              error: (err) => {
                this.snackBar.open(
                  err.error || 'Error creating purchase order',
                  'Dismiss',
                  {
                    duration: SnackBarConfig.ERROR.duration,
                  },
                );
              },
            });
        },
      });
  }

  onToggleTransferOrderDialog(data :{
    parentSelectedRows :any[];
    childSelectedRows :any[];
  }) :void {
    const { parentSelectedRows = [], childSelectedRows = [] } = data;
    const orderItems :IOrderCreateItem[] = [
      ...parentSelectedRows.map((item) => ({
        itemId: item.id,
        orderItemId: item.orderItemId,
        orderId: item.orderId,
        itemType: 'sales_order_item',
      })),
      ...childSelectedRows.map((item) => ({
        itemId: item.id,
        orderItemId: item.orderItemId,
        orderId: item.orderId,
        itemType: 'bom_item',
      })),
    ];
    this.dialog
      .open(SalesOrderLineItemsTransferOrderDialogComponent, {
        width: DialogConfig.Size.SMALL,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe({
        next: (response :any) => {
          if (response) {
            this.onCreateTransferOrder({
              orderItems,
              warehouseId: response.id,
            });
          }
        },
      });
  }

  onCreateTransferOrder(data :{ orderItems :any[]; warehouseId :number }) :void {
    const { orderItems, warehouseId } = data;
    this.snackBar.open('Creating transfer order...', 'Dismiss');
    this.orderApiService
      .createFromItems({
        sourceOrderType: EOrderType.SALES_ORDER,
        destinationOrderType: EOrderType.TRANSFER_ORDER,
        orderItems,
        sourceWarehouseId: warehouseId,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.snackBar.open(
            response?.success || 'Transfer order created successfully',
            'Dismiss',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
        error: (err) => {
          this.snackBar.open(
            err?.error || 'Error creating transfer order',
            'Dismiss',
            {
              duration: SnackBarConfig.ERROR.duration,
            },
          );
        },
      });
  }

  onToggleAssignTagsDialog(data :any) :void {
    this.dialog
      .open(TagsAutocompleteChipDialogComponent, {
        width: DialogConfig.Size.SMALL,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe({
        next: (tags :IValueLabel[]) => {
          if (tags?.length) {
            this.tagApiService
              .replaceBulk({
                taggableType: EOrderTaggableType.SALES_ORDER_LINE_ITEM,
                taggableIds: data?.parentSelectedRows?.map((item :any) => item.id),
                tagIds: tags.map((tag) => tag.value),
              })
              .pipe(takeUntil(this.onDestroy$))
              .subscribe({
                next: (response) => {
                  this.#loadOrderLineItems();
                  this.snackBar.open(
                    response?.success || 'Tags updated successfully',
                    'Dismiss',
                    {
                      duration: SnackBarConfig.DEFAULT.duration,
                    },
                  );
                },
                error: (err) => {
                  this.snackBar.open(
                    err?.error || 'Error updating tags',
                    'Dismiss',
                    {
                      duration: SnackBarConfig.ERROR.duration,
                    },
                  );
                },
              });
          }
        },
      });
  }

  // TODO: Move this to on demand and separate component
  #loadOrderLineItems() :void {
    this.loadingLineItems$.next(true);
    this.salesOrderApiservice
      .getLineItems({
        relations: [
          'order',
          'orderedUom',
          'rfq.preferredVendor',
          'rfq.boms',
          'rfq.tags.tag',
          'tags',
        ].join('|'),
        ...this.linteItemsQueryParams,
      })
      .subscribe({
        next: (response :any) => {
          const data :IOrderData[] = response?.data?.map((item :any) => ({
            boms: item?.rfq?.boms || [],
            id: item.id,
            name: item.name,
            orderId: item.salesOrder.id,
            orderItemId: item.id,
            orderNumber: item?.salesOrder?.orderNumber,
            quantity: item.orderedQuantity,
            rfqId: item.rfqId,
            rfqTag:
              item?.rfq?.rfqTags?.map((rfqTag :any) => rfqTag?.tag?.name) || [],
            sku: item.sku,
            stocks: item.numberOfStocks,
            tag: item?.tags?.map((tag :any) => tag.name),
            supplier: item?.rfq?.preferredVendor?.factoryName,
            uom: item?.orderedUom?.name,
          }));
          this.lineItemRowData = {
            data,
            meta: response?.meta,
          };
        },
        error: (err) => {
          this.snackBar.open('Error loading order line items', 'Dismiss', {
            duration: SnackBarConfig.ERROR.duration,
          });
        },
        complete: () => {
          this.loadingLineItems$.next(false);
        },
      });
  }

  onLineItemPaginationEvent(pageEvent :IOrderAgTableLineItemsPageEvent) :void {
    this.linteItemsQueryParams = {
      ...this.linteItemsQueryParams,
      tag: pageEvent?.tag || '',
      supplier: pageEvent?.supplier || '',
      page: (pageEvent.pageIndex + 1)?.toString(),
      pageSize: pageEvent.pageSize?.toString(),
    };
    this.#loadOrderLineItems();
  }

  private loadSalesOrders() :void {
    this.loading$.next(true);
    this.gridOptions.api?.showLoadingOverlay();
    this.orderApiService
      .get(this.currentQueryParams)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          if (response) {
            const { data, meta } = response;

            let channelIntegrationMapping = null;

            data.forEach((row :ISalesOrder) => {
              channelIntegrationMapping = this.selectedAppIntegrationAccount
                ? row.channel?.channelIntegrationMapping.find(
                    (mapping :any) =>
                      mapping.appIntegrationAccountId ===
                      this.selectedAppIntegrationAccount,
                  )
                : row.channel?.channelIntegrationMapping[0];
            });

            this.pagination = meta;

            if (this.groupBy) {
              this.gridOptions.groupDefaultExpanded = 1;
              this.updateCustomGroupedRows();
            } else {
              this.gridOptions.groupDefaultExpanded = 0;
            }

            if (this.isSelectAllRows) {
              this.selectedRows = [];
            }

            this.agGridPaginationUtilService.notifyPaginationChange({
              pagination: this.pagination,
              selectedRows: this.selectedRows,
              isSelectAllRows: this.isSelectAllRows,
            });

            if (this.currentOnDemandRelation) {
              // Find and update the relevant column
              this.rowData = this.rowData.map((row) => {
                const salesOrder = data.find(
                  (order :ISalesOrder) => order.id === row.id,
                );

                if (salesOrder) {
                  const column = this.currentOnDemandRelation?.split('|')[0];

                  return {
                    ...row,
                    [column as string]: salesOrder[column as string],
                    appIntegrationAccountId:
                      channelIntegrationMapping?.appIntegrationAccountId,
                  };
                } else {
                  return {
                    ...row,
                    appIntegrationAccountId:
                      channelIntegrationMapping?.appIntegrationAccountId,
                  };
                }
              });

              this.currentOnDemandRelation = null;
              this.setCurrentQueryParams();
            } else {
              this.rowData = data.map((row :ISalesOrder) => {
                return {
                  ...row,
                  appIntegrationAccountId:
                    channelIntegrationMapping?.appIntegrationAccountId,
                };
              });
            }
          }

          this.gridOptions.api?.hideOverlay();
          this.loading$.next(false);
          this.cd.detectChanges();
        },
        error: () => {
          this.gridOptions.api?.hideOverlay();
          this.loading$.next(false);
          this.cd.detectChanges();
        },
      });
  }

  private loadAllSelectableRowCount() :void {
    this.orderSalesApiService
      .getAllSelectableRowCount(this.currentQueryParams)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          if (response) {
            this.totalRowCount = response;
          }
          this.cd.detectChanges();
        },
        error: () => {
          this.cd.detectChanges();
        },
      });
  }

  private removeColumnByField(field :string) :void {
    const columnDefs = this.gridOptions.columnDefs;
    const newColumnDefs = columnDefs.filter((col :any) => col.field !== field);
    this.gridApi?.setColumnDefs(newColumnDefs);
  }

  private updateCustomGroupedRows() :void {
    const colDefs = this.gridApi?.getColumnDefs() as ColDef[];
    const newColDefs = colDefs.map((newColDef, index) => {
      const colDef = colDefs.find((col :any) => col.field === newColDef.field);
      if (newColDef.rowGroup) {
        return {
          ...newColDef,
          hide: true,
        };
      }

      return {
        ...newColDef,
        hide: colDef?.hide === undefined ? false : colDef.hide,
      };
    });

    const groupByColumnExist = colDefs.find(
      (colDef) => colDef.field === this.currentGroupOption?.field,
    );
    if (!groupByColumnExist) {
      newColDefs.push({
        headerName: this.currentGroupOption?.headerName,
        field: this.currentGroupOption?.field,
        rowGroup: true,
        hide: true,
        lockVisible: true,
        filter: false,
        sortable: false,
        valueGetter: (params :any) :string => {
          const value = params.data ? params.data[this.groupBy] : null;
          if (!value) {
            return '';
          }
          const group = this.groups.find((g) => value === g.label);

          return group?.label || value;
        },
      });
    }
    this.gridApi?.refreshCells();
    this.gridApi?.setColumnDefs(newColDefs);
  }

  private fetchGroupedSalesOrders() :void {
    this.gridOptions.api?.showLoadingOverlay();

    const { defaultGroupQueryParams } = SalesOrderAgTableConfig;

    const params = {
      ...this.currentQueryParams,
      groupBy: this.groupBy,
      limit: defaultGroupQueryParams.limit,
      page: this.groups.length
        ? Math.floor(
            this.groups.length / Number(defaultGroupQueryParams.limit) + 1,
          ).toString()
        : defaultGroupQueryParams.page,
    };

    this.orderApiService
      .getGroups(params)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          if (response) {
            if (this.groups.length) {
              this.groups.push(...response.data);
            } else {
              this.groups = response.data;
              this.gridOptions.groupRowRendererParams.groups = this.groups;
            }

            this.gridOptions.api?.hideOverlay();
            if (
              Number(params.page) <= response.meta.last_page &&
              this.currentGroupsTotalCount < this.expectedRecordsCount
            ) {
              this.fetchGroupedSalesOrders();
            } else {
              this.onRefetchTableData();
            }
          }
        },
        error: () => {
          this.snackBar.open('Failed to fetch grouped orders', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
          this.gridOptions.api?.hideOverlay();
        },
      });
  }

  private attachEventListenersOnPageStateChange() :void {
    // attach event listeners on filter, sort, column visibility change
    this.gridOptions.api?.addEventListener(
      'filterChanged',
      this.pageStateEvent.bind(this),
    );
    this.gridOptions.api?.addEventListener(
      'sortChanged',
      this.pageStateEvent.bind(this),
    );
    this.gridOptions.api?.addEventListener('columnVisible', (event :any) => {
      this.columnVisibilityChangeSubject.next(event);
    });

    this.columnVisibilityChangeSubject
      .pipe(debounceTime(500), takeUntil(this.onDestroy$))
      .subscribe((event :any) => {
        this.pageStateEvent(event);
      });
  }

  private pageStateEvent(event :any) :void {
    const pageState = {
      filterModel: this.currentFilterModel,
      columnState: this.currentColumnState,
      activeCustomFilters: this.currentActiveCustomFilters,
    };

    this._viewSettings.updateState({ agFilterModel: this.currentFilterModel });

    localStorage.setItem(
      LocalStorageKeys.SALES_ORDER_AG_STATE,
      JSON.stringify(pageState),
    );

    switch (event.type) {
      case 'filterChanged':
        event.columns.forEach((column :any) => {
          if (!column.visible) {
            this.gridColumnApi.setColumnVisible(column.colId, true);
          }
        });
        this.resetPaginationState();

        if (this.groupBy) {
          this.pagination = {
            ...this.pagination,
            current_page: 1,
          };
          this.groups = [];
          this.setCurrentQueryParams();
          this.fetchGroupedSalesOrders();
        } else {
          this.onRefetchTableData();
        }
        break;
      case 'sortChanged':
        this.resetPaginationState();
        if (this.groupBy) {
          this.pagination = {
            ...this.pagination,
            current_page: 1,
          };
        }
        this.onRefetchTableData();
        break;
      case 'columnVisible':
        let updatedOnDemandParams = false;
        if (this.activeColumns?.length) {
          this.columns = [...SalesOrderAgTableConfig.columns];
          this.relations = [...SalesOrderAgTableConfig.relations];

          this.activeColumns.forEach((column :string) => {
            this.updateOnDemandParamFromColumn(column);
          });
          updatedOnDemandParams = true;
        }
        if (event.visible && updatedOnDemandParams) {
          this.onRefetchTableData();
        }
        break;

      default:
        this.onRefetchTableData();
        break;
    }

    this.agGridStateChangedEvent.emit();
  }

  private initColumnDefs() :void {
    const columnDefs = this.columnDefs;
    columnDefs.push({
      headerName: 'Invoice Status',
      field: this.fields.INVOICE_STATUS,
      sortable: true,
      filterParams: {
        values: SalesOrderConfig.invoiceStatusTypes.map((row :IIdName) => row.id),
        valueFormatter: ({ value }) => {
          const invoiceStatus = SalesOrderConfig.invoiceStatusTypes.find(
            (row :IIdName) => row.id === value,
          );

          return invoiceStatus?.name;
        },
      },
      filter: true,
      hide: true,
      valueGetter: (params :any) => params?.data?.invoiceStatus ?? '',
      cellRenderer: (params :any) => {
        const invoiceStatus =
          params?.data?.invoiceStatus.replace(/_/g, ' ').toLowerCase() || '';

        return `${toCapitalize(invoiceStatus, true)}`;
      },
      width: 140,
    });

    if (!this.storedPageState?.columnState?.length) {
      return;
    }

    let columnState = this.storedPageState?.columnState;

    // remove column if not exist in columnDefs
    columnState = columnState?.filter(
      (column :any) =>
        column.colId?.startsWith('cf__') ||
        (!column.colId?.startsWith('cf__') &&
          columnDefs.findIndex((colDef :any) => colDef.field === column.colId) >=
            0),
    );

    columnDefs.forEach((colDef :any) => {
      const column = columnState.find(
        (column :any) => column.colId === colDef.field,
      );

      if (!column) {
        columnState.push({
          colId: colDef.field,
          width: colDef.minWidth ?? 100,
          hide: colDef.hide ?? false,
          pinned: colDef.pinned ?? null,
          sort: null,
          sortIndex: null,
          aggFunc: null,
          rowGroup: colDef.rowGroup ?? false,
          rowGroupIndex: null,
          pivot: false,
          pivotIndex: null,
          flex: null,
        });
      }
    });

    localStorage.setItem(
      LocalStorageKeys.SALES_ORDER_AG_STATE,
      JSON.stringify({
        ...this.storedPageState,
        columnState,
      }),
    );
  }

  private initOverrideColumns() :void {
    if (this.storedPageState?.columnState?.length) {
      this.columnDefs = this.storedPageState.columnState
        .filter(
          (column :ColumnState) =>
            !column.colId?.startsWith('cf__') &&
            !column.colId?.startsWith('ag-Grid') &&
            column.colId !== this.groupBy,
        )
        .map((column :ColumnState) => {
          const currentColumn = this.columnDefs.find(
            (col) => col.field === column.colId,
          );

          if (
            !column?.hide &&
            Object.keys(this.onDemandServerSideRelations).includes(
              currentColumn?.field as string,
            ) &&
            !this.relations.includes(
              this.onDemandServerSideRelations[currentColumn?.field as string],
            )
          ) {
            this.relations.push(
              this.onDemandServerSideRelations[currentColumn?.field as string],
            );
          }

          return {
            ...currentColumn,
            hide: column.hide,
          };
        });
    }
  }

  private initGroupState() :void {
    const groupState = localStorage.getItem(
      LocalStorageKeys.SALES_ORDER_AG_GROUP_STATE,
    );
    if (groupState) {
      this.groupBy = groupState;
    }
  }

  private setCurrentQueryParams() :any {
    const filters :{ [key :string] :any } = {};

    if (this.currentFilterModel) {
      const _default = (key :string) => {
        filters[key] = this.currentFilterModel[key].filter?.toString();
      };

      Object.keys(this.currentFilterModel).forEach((key) => {
        switch (key) {
          // use the new filters
          case this.fields.SKU:
            if (this._feature.isActive('utah-UTAH-356-so_grid_advanced_filters')) {
              Object.assign(
                filters,
                AgGridUtils.buildAdvancedFilters(
                  pick(this.currentFilterModel, [key]) as IAgGridFilterModelValue,
                  {
                    allowMultiValueFilters: true,
                  },
                ),
              );
            } else {
              _default(key);
            }
            break;
          case this.fields.WAREHOUSE_GROUP:
            filters['warehouseGroupIds'] =
              this.currentFilterModel[key].values.join(',');
            break;
          case this.fields.ORDER_DATE:
          case this.fields.VERIFIED_DATE:
          case this.fields.SHIP_DATE: {
            const value = new Date(this.currentFilterModel[key]?.dateFrom);
            value.setHours(0, 0, 0);
            filters[key] = value.toISOString();
            break;
          }
          case this.fields.ORDER_STATUSES:
          case this.fields.PICKING_STATUS:
          case this.fields.APPROVAL_STATUS:
          case this.fields.IS_POSTED:
          case this.fields.SOURCE:
          case this.fields.INVOICE_PAYMENT_STATUS:
          case this.fields.TYPE:
          case this.fields.CHANNEL_IDS:
          case this.fields.INVOICE_STATUS:
          case this.fields.TAGS:
            filters[key] = this.currentFilterModel[key].values.join(',');
            break;
          case this.fields.APP_INTEGRATION_ACCOUNT_ID: {
            const appIntegrationAccountId = this.currentFilterModel[key];
            if (appIntegrationAccountId === 'None') {
              delete filters[this.fields.APP_INTEGRATION_ACCOUNT_ID];
              this.selectedAppIntegrationAccount = null;
              const updatedFilterModel = {
                ...this.currentFilterModel,
              };
              delete updatedFilterModel[this.fields.APP_INTEGRATION_ACCOUNT_ID];
              this.gridApi?.setFilterModel(updatedFilterModel);
            } else {
              this.selectedAppIntegrationAccount = appIntegrationAccountId;
            }
            break;
          }
          default:
            _default(key);
            break;
        }
      });
    }

    if (this.orderStatus) {
      filters[this.fields.ORDER_STATUSES] = this.orderStatus;
    } else {
      const customFilters = this.currentActiveCustomFilters;
      if (customFilters?.orderStatuses) {
        filters[this.fields.ORDER_STATUSES] = customFilters.orderStatuses;
      }
    }

    if (this.selectedAppIntegrationAccount) {
      filters[this.fields.APP_INTEGRATION_ACCOUNT_ID] =
        this.selectedAppIntegrationAccount;
    }
    this.currentQueryParams = {
      ...SalesOrderAgTableConfig.defaultQueryParams,
      ...filters,
      relations: this.relations.join('|'),
      columns: this.columns.join(','),
    };

    const sortedColumn = this.getSortedModel();

    if (sortedColumn) {
      let sortField = sortedColumn.colId;

      switch (sortField) {
        case 'warehouseGroup.id':
          sortField = 'warehouse_group_id';
          break;

        default:
          break;
      }

      this.currentQueryParams = {
        ...this.currentQueryParams,
        sortField,
        sortDirection: sortedColumn.sort,
      };
    }

    if (this.storedPagination?.pageSize) {
      this.currentQueryParams = {
        ...this.currentQueryParams,
        page: this.storedPagination.pageIndex + 1,
        limit: this.storedPagination.pageSize,
      };
    }

    if (this.groupBy) {
      this.currentQueryParams = {
        ...this.currentQueryParams,
        groupBy: this.groupBy,
        sortDirection: sortedColumn?.sort || 'asc',
      };
    }

    if (this.currentOnDemandRelation) {
      this.currentQueryParams = {
        ...this.currentQueryParams,
        relations: this.currentOnDemandRelation,
      };
    }
  }

  private getSortedModel() :any {
    const sortedColumn = this.currentColumnState?.find(
      (col :any) => col.sort !== null,
    );

    switch (sortedColumn?.colId) {
      case this.fields.ORDER_STATUS:
        //Transform orderStatuses to orderStatus due to no orderStatuses on backend when sorting
        sortedColumn.colId = this.fields.ORDER_STATUS;
        break;

      default:
        sortedColumn?.colId;
        break;
    }

    return sortedColumn;
  }

  private handleWarehouseGroupFilterUpdate() :void {
    // Locate the Warehouse Group column and update its filterParams
    const warehouseGroupColumn = this.columnDefs.find(
      (col) => col.field === this.fields.WAREHOUSE_GROUP,
    );
    if (warehouseGroupColumn) {
      warehouseGroupColumn.filterParams = {
        values: this.warehouseIds,
        valueFormatter: (data :any) => {
          const channel = this.warehouseGroups.find(
            (row :IWarehouseGroup) => row.id === data.value,
          );

          return channel?.label;
        },
      };
    }

    //Update the Column Definitions in the Grid
    this.gridApi?.setColumnDefs(this.columnDefs);

    //Refresh the Filter UI
    // this.gridApi?.destroyFilter(this.fields.WAREHOUSE_GROUP);
  }

  private handleOMSFilterUpdate(
    appIntegrationAccounts :IAppIntegrationAccount[],
  ) :void {
    // Locate the Warehouse Group column and update its filterParams
    const omsColumn = this.columnDefs.find(
      (col) => col.field === this.fields.APP_INTEGRATION_ACCOUNT_ID,
    );

    if (omsColumn) {
      const currentAppIntegrationAccountId =
        this.currentFilterModel[this.fields.APP_INTEGRATION_ACCOUNT_ID];
      omsColumn.filterParams = {
        values: appIntegrationAccounts.map((row :IAppIntegrationAccount) => ({
          id: row.id,
          label: row.label,
          isDefault: currentAppIntegrationAccountId
            ? row.id === currentAppIntegrationAccountId
            : false,
        })),
      };
    }

    //Update the Column Definitions in the Grid
    this.gridApi?.setColumnDefs(this.columnDefs);

    //Refresh the Filter UI
    this.gridApi?.destroyFilter(this.fields.APP_INTEGRATION_ACCOUNT_ID);
  }

  private handleChannelFilterUpdate() :void {
    const channelColumn = this.columnDefs.find(
      (col) => col.field === this.fields.CHANNEL_IDS,
    );
    if (channelColumn) {
      channelColumn.filterParams = {
        values: this.activeChannelIds,
        valueFormatter: (data :any) => {
          const channel = this.channels.find(
            (row :IChannel) => row.id === data.value,
          );

          return channel?.label;
        },
      };
    }

    //Update the Column Definitions in the Grid
    this.gridApi?.setColumnDefs(this.columnDefs);

    //Refresh the Filter UI
    this.gridApi?.destroyFilter(this.fields.CHANNEL_IDS);
  }

  #handleTagFilterUpdate() :void {
    const tagColumn = this.columnDefs.find((col) => col.field === this.fields.TAGS);
    if (tagColumn) {
      tagColumn.filterParams = {
        values: this.tagIds,
        valueFormatter: (data :any) => {
          const tag = this.tags.find((row :ITagOld) => row.id === data?.value);

          return tag?.name;
        },
      };
    }

    //Update the Column Definitions in the Grid
    this.gridApi?.setColumnDefs(this.columnDefs);

    //Refresh the Filter UI
    this.gridApi?.destroyFilter(this.fields.TAGS);
  }

  private updateOrderStatusFilter(orderStatus :string) :void {
    const columnState = this.storedPageState?.columnState || [];
    const currentFilters = this._viewSettings.get('agFilterModel') ?? {};
    const relevantColumns = Object.keys(currentFilters).filter(
      (col) => columnState.find(({ colId }) => colId === col).hide,
    );

    const updatedStoredPageState = {
      ...(this.storedPageState || {}),
      filterModel: {
        ...(this.storedPageState?.filterModel || {}),
        ...this.currentFilterModel,
        ...pick(currentFilters, relevantColumns),
      },
    };

    if (orderStatus) {
      updatedStoredPageState.activeCustomFilters = {
        orderStatuses: orderStatus,
      };
    }

    localStorage.setItem(
      LocalStorageKeys.SALES_ORDER_AG_STATE,
      JSON.stringify(updatedStoredPageState),
    );

    const updatedFilter = {
      ...this.currentFilterModel,
      ...updatedStoredPageState.filterModel,
    };

    if (orderStatus === null) {
      delete updatedFilter[this.fields.ORDER_STATUSES];
    } else {
      updatedFilter[this.fields.ORDER_STATUSES] = {
        filterType: 'set',
        values: [orderStatus],
      };

      const columnDefs = this.storedPageState?.columnState || this.columnDefs;
      const orderStatusColumn = columnDefs.find(
        (col :any) => col.field === this.fields.ORDER_STATUSES,
      );

      if (orderStatusColumn) {
        orderStatusColumn.filterParams = {
          values: [orderStatus],
          valueFormatter: (data :any) => {
            const orderStatus = SalesOrderConfig.orderStatus.find(
              (row :IIdName) => row.id === data.value,
            );

            return orderStatus?.name;
          },
          newRowsAction: 'keep',
        };
        this.gridApi?.setColumnDefs(columnDefs);
      }
    }

    if (this.gridApi) {
      this.gridApi?.setFilterModel({
        ...this._viewSettings.get('agFilterModel'),
        ...updatedFilter,
      });

      // Get current sort state
      const currentSortState = this.gridOptions.columnApi?.getColumnState();
      this.gridOptions.columnApi?.applyColumnState({
        state: currentSortState || [],
        applyOrder: true,
      });
    }
    // this.onRefetchTableData();
  }

  private applyBackgroundColor(data :{
    id :number;
    backgroundColor :string;
  }) :void {
    this.orderApiService
      .updateBackgroundColor(data)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          this.snackBar.open('Background color saved.', 'Success', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
        error: (error) => {
          this.snackBar.open('Error saving background color.', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private loadCustomFields() :void {
    this.customFieldApiService
      .getAll()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((customFields) => {
        if (customFields) {
          this.setCustomFieldCells(customFields);
        }
      });
  }

  private getAppIntegrationAccounts() :void {
    this.appIntegrationAccountApiService
      .get({})
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.appIntegrationAccounts = response.data;
          this.handleOMSFilterUpdate(response.data);
        },
        error: () => {
          this.snackBar.open('Failed to fetch app integration accounts', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private setCustomFieldCells(customFields :ICustomField[]) :void {
    const columnDefs = this.gridApi?.getColumnDefs() || this.columnDefs;
    const customFieldColumnsState =
      this.storedPageState?.columnState?.filter((item :any) =>
        item.colId?.startsWith('cf__'),
      ) || [];

    customFields.forEach((field :ICustomField) => {
      const columnState = customFieldColumnsState.find(
        (item :any) => item.colId === 'cf__' + field.label,
      );

      columnDefs?.push({
        headerName: field.label,
        field: 'cf__' + field.label,
        sortable: false,
        filter: false,
        hide: columnState ? columnState.hide : true,
        valueFormatter: (params :any) => {
          const customField = params?.data?.customFieldValues.find(
            (cf :any) => cf.customField.label === field.label,
          );
          const customValue = this.customFieldParserPipe.transform(customField);

          return customValue !== null ? String(customValue) : '';
        },
        cellEditorFramework: SalesOrderCustomFieldCellEditorComponent,
        width: 120,
        cellEditorParams: {
          customFieldLabel: field.label,
          customFields: customFields,
        },
        editable: true,
        cellStyle: (params :any) => ({
          overflow: 'visible',
        }),
      });

      if (
        columnState &&
        !columnState?.hide &&
        !this.relations.includes(
          this.onDemandServerSideRelations[this.fields.CUSTOM_FIELDS],
        )
      ) {
        this.relations.push(
          this.onDemandServerSideRelations[this.fields.CUSTOM_FIELDS],
        );
      }
    });

    this.gridApi?.setColumnDefs(columnDefs);
    this.columnDefs = columnDefs; // fix [CS-4716]
  }

  private resetPaginationState() :void {
    const updatedPaginationState = {
      ...this.storedPagination,
      pageIndex: 0,
      previousPageIndex: 1,
    };

    localStorage.setItem(
      LocalStorageKeys.SALES_ORDER_TABLE_PAGINATION,
      JSON.stringify(updatedPaginationState),
    );

    this.gridApi?.paginationGoToPage(1);
  }

  private getConsolidatedOrderItems() :void {
    const orderIds :number[] = this.selectedRows
      .filter((selectedRow :ISalesOrder | null) => !!selectedRow)
      .map((selectedRow :ISalesOrder) => selectedRow.id);

    this.loadingConsolidatedItems = true;

    if (orderIds.length > 0) {
      this.picklistApiService
        .getPickingItemsBySalesOrderIds({
          salesOrderIds: orderIds.join(','),
          includeWithPicklist: 'true',
        })
        .pipe(
          takeUntil(this.onDestroy$),
          finalize(() => {
            this.loadingConsolidatedItems = false;
            this.cd.detectChanges();
          }),
        )
        .subscribe({
          next: (orderSalesPicklistPreview :IOrderSalesPicklistPreview) => {
            this.pickingItems = orderSalesPicklistPreview;
            this.consolidatedOrderItems =
              orderSalesPicklistPreview?.pickingItems || [];
            this.warehouseAddress =
              orderSalesPicklistPreview?.warehouseAddress || null;
          },
          error: (response) => {
            this.consolidatedOrderItems = [];
            this.snackBar.open(response.error, 'Fail', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          },
        });
    } else {
      this.consolidatedOrderItems = [];
      this.cd.detectChanges();
    }
  }

  private editSalesOrder(salesOrderId :number) :void {
    this._actions
      .editOrder(salesOrderId)
      .pipe(take(1))
      .subscribe((response) => {
        if (response) {
          this.onRefetchTableData();
        }
      });
  }

  private deleteSalesOrder(id :number) :void {
    let rollbackInv :number = 0;

    const data :ISalesOrderConfirmationDialogData = { salesOrderIds: [id] };

    this.dialog
      .open(SalesOrderConfirmationDialogComponent, {
        ...DialogConfig.MEDIUM,
        data,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(
        (
          result :| {
                dismiss :boolean;
                errors ?:string[];
                value ?:boolean;
              }
            | boolean,
        ) => {
          if (typeof result === 'object' && !result.dismiss) {
            if (result.value === true) {
              rollbackInv = 1;
            }

            this.loadingDialogService.show('Deleting Sales Order, please wait...');

            this.orderPostedApiService
              .delete(id, { rollbackInv: rollbackInv })
              .pipe(takeUntil(this.onDestroy$))
              .subscribe({
                next: (response) => {
                  this.loadingDialogService.close();

                  this.snackBar.open(response.message, 'Success', {
                    duration: SnackBarConfig.DEFAULT.duration,
                  });

                  // refreshTable
                  this.onRefetchTableData();
                },
                error: (response) => {
                  this.loadingDialogService.close();

                  this.snackBar.open(response.error, 'Fail', {
                    duration: SnackBarConfig.DEFAULT.duration,
                  });

                  this.onRefetchTableData();
                },
              });
          }
        },
      );
  }

  private openCreateTrackingNumber(data :{
    salesOrderId :number;
    trackingNumbers :string[];
  }) :void {
    this.createTrackingNumberEvent.emit(data);
  }

  private setToPending(salesOrderId :number) :void {
    this.setToPendingEvent.emit(salesOrderId);
  }

  private holdOrder(data :{
    id :number;
    orderNumber :string;
    source :string;
    shipstationOrderId ?:any;
  }) :void {
    this.holdOrderEvent.emit(data);
  }

  private openPrintShippingLabel(salesOrder :any) :void {
    this.printShippingLabelEvent.emit(salesOrder);
  }

  private createSalesOrderInvoice(shipstationOrderId :number) :void {
    this.dialog.open(CreateInvoiceFromSalesOrderFormComponent, {
      data: {
        salesOrderId: shipstationOrderId,
      },
      width: DialogConfig.sizes['xx-large'],
    });
  }

  private updateCustomFields(data :ISalesOrder) :void {
    this.dialog
      .open(CustomFieldFormGroupComponent, {
        width: DialogConfig.sizes.medium,
        data: {
          modelId: data.id,
          modelType: EModelType.SALES_ORDER,
          customFieldValues: data.customFieldValues || [],
          customFieldObject: ECustomFieldObject.SALES_ORDER,
        },
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.onRefetchTableData();
        }
      });
  }

  private processOnRowSplice(event :any) :void {
    if (!this.isSelectAllRows) {
      const index = this.selectedRows.findIndex(
        (order) => order.id === event.data.id,
      );

      if (event.node.selected && index === -1) {
        this.selectedRows = [...this.selectedRows, event.data];
        // this.selectedRows.push(event.data);
      } else if (!event.node.selected && index !== -1) {
        this.selectedRows.splice(index, 1);
      }

      return;
    }

    if (!event.node.selected) {
      this.selectedRows = this.selecatableRowData;
      const index = this.selectedRows.findIndex(
        (order) => order.id === event.data.id,
      );

      this.selectedRows.splice(index, 1);
      this.isSelectAllRows = false;
    }
  }

  private sendEdiTransactionDocument(
    salesOrderId :number,
    ediTransactionNumber :string,
  ) :void {
    this.loadingDialogService.show(
      'Generating and sending EDI file, please wait...',
    );
    this.orderApiService
      .sendEdiTransactionDocument(salesOrderId, ediTransactionNumber)
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          this.loadingDialogService.close();
          this.snackbar.open(
            response.success ?? 'EDI file generated and sent successfully',
            'Success',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
        error: (error) => {
          this.loadingDialogService.close();
          this.snackbar.open(
            error.error ?? 'Error sending EDI document.',
            'Error',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );
        },
      });
  }

  loadShipstationShipments(salesOrderId :any) :void {
    this.orderApiService
      .getById(salesOrderId, {
        relations: 'shipments,availableShippingLabel,shipstationShipments',
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          this.shipstationShipments = response?.shipstationShipments;
        },
        error: (error) => {
          this.snackbar.open('Error loading sales order.', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  updateTags(salesOrderId :number) :void {
    this.orderApiService
      .getById(salesOrderId, {
        relations: 'tags',
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response :any) => {
          this.dialog
            .open(OrderPostedTagsFormComponent, {
              data: {
                selected: response?.tags.map((item :ITag) => item.id) || [],
              },
              width: DialogConfig.sizes.small,
            })
            .afterClosed()
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((tags) => {
              if (tags) {
                this.loadingDialogService.show(
                  'Updating Sales Order Tags, please wait...',
                );

                this.orderSalesApiService
                  .bulkUpdateTags({
                    selectedIds: [salesOrderId],
                    tags: tags,
                  })
                  .pipe(takeUntil(this.onDestroy$))
                  .subscribe({
                    next: (response) => {
                      this.loadingDialogService.close();

                      this.snackBar.open(response.success, 'Success', {
                        duration: SnackBarConfig.DEFAULT.duration,
                      });

                      this.onRefetchTableData();
                    },
                    error: (response) => {
                      this.loadingDialogService.close();

                      this.snackBar.open(response.error, 'Fail', {
                        duration: SnackBarConfig.DEFAULT.duration,
                      });

                      this.onRefetchTableData();
                    },
                  });
              }
            });
        },
        error: (error) => {
          this.snackbar.open('Error loading sales order.', 'Error', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  approveOrder(data :ISalesOrder) :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Approve Order',
          message: "This action will flag the sales order as 'Approved'. Proceed?",
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          this.loadingDialogService.show('Approving order...');

          this.approvalApiService
            .updateApprovalStatus(data.approval.id, {
              status: EApprovalStatusValue.APPROVED,
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response) => {
                this.loadingDialogService.close();

                this.snackBar.open(
                  response.success || 'Sales order successfully approved',
                  'Success',
                  {
                    duration: SnackBarConfig.DEFAULT.duration,
                  },
                );

                // refreshTable
                this.onRefetchTableData();
              },
              error: (response) => {
                this.loadingDialogService.close();

                this.snackBar.open(response.error, 'Fail', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });

                this.onRefetchTableData();
              },
            });
        }
      });
  }

  rejectOrder(data :ISalesOrder) :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Reject Order',
          message: "This action will flag the sales order as 'Rejected'. Proceed?",
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          this.loadingDialogService.show('Rejecting order...');

          this.approvalApiService
            .updateApprovalStatus(data.approval.id, {
              status: EApprovalStatusValue.REJECTED,
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response) => {
                this.loadingDialogService.close();

                this.snackBar.open(
                  response.success || 'Sales order successfully rejected',
                  'Success',
                  {
                    duration: SnackBarConfig.DEFAULT.duration,
                  },
                );

                // refreshTable
                this.onRefetchTableData();
              },
              error: (response) => {
                this.loadingDialogService.close();

                this.snackBar.open(response.error, 'Fail', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });

                this.onRefetchTableData();
              },
            });
        }
      });
  }

  flagOrder(salesOrderId :number) :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Flag Order',
          message: "This action will flag the sales order as 'Flagged'. Proceed?",
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          this.loadingDialogService.show('Flagging order...');

          this.picklistApiService
            .flagSalesOrder({
              salesOrderId: salesOrderId,
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response) => {
                this.loadingDialogService.close();

                this.snackBar.open(response.success, 'Success', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });

                // refreshTable
                this.onRefetchTableData();
              },
              error: (response) => {
                this.loadingDialogService.close();

                this.snackBar.open(response.error, 'Fail', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });

                this.onRefetchTableData();
              },
            });
        }
      });
  }

  unFlagOrder(salesOrderId :number) :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Unflag Order',
          message:
            "This action will unflag the sales order as 'Unflagged'. Proceed?",
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          this.loadingDialogService.show('Unflagging order...');

          this.picklistApiService
            .unflagSalesOrder({
              salesOrderId: salesOrderId,
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response) => {
                this.loadingDialogService.close();

                this.snackBar.open(response.success, 'Success', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });

                // refreshTable
                this.onRefetchTableData();
              },
              error: (response) => {
                this.loadingDialogService.close();

                this.snackBar.open(response.error, 'Fail', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });

                this.onRefetchTableData();
              },
            });
        }
      });
  }

  onAppIntegrationAccountChange(value :number | null) :void {
    this.selectedAppIntegrationAccount = value;
    this.selectedRows = [];
    this.gridApi?.deselectAll();

    if (value) {
      this.setCurrentQueryParams();
      this.loadSalesOrders();
    } else {
      this.onRefetchTableData();
    }
  }

  pushFulfillment(salesOrderId :number) :void {
    this.salesOrderFulfillmentFacadeService.pushFulfillment(salesOrderId, () => {
      this.onRefetchTableData();
    });
  }

  onUnpostOrder(salesOrderId :number) :void {
    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Unallocate Order',
          message:
            'This action will unallocate the sales order and will be reposted in Purgatory Page. Proceed?',
        },
        width: DialogConfig.sizes.small,
      })
      .afterClosed()
      .pipe(take(1))
      .subscribe((confirm :boolean) => {
        if (confirm) {
          this.loadingDialogService.show('Unposting order...');
          this.orderSalesApiService
            .unpostOrders({
              salesOrderIds: [salesOrderId.toString()],
            })
            .pipe(takeUntil(this.onDestroy$))
            .subscribe({
              next: (response) => {
                this.loadingDialogService.close();
                this.snackBar.open(response.success, 'Success', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });

                this.onRefetchTableData();
              },
              error: (response) => {
                this.loadingDialogService.close();
                this.snackBar.open(response.error, 'Fail', {
                  duration: SnackBarConfig.DEFAULT.duration,
                });

                this.onRefetchTableData();
              },
            });
        }
      });
  }

  #connectGridContext() {
    this._viewSettings.state
      .observe('prefersTagsCollapsed')
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((prefersTagsCollapsed :boolean) => {
        this.gridOptions.context.tagsAreCollapsed = prefersTagsCollapsed;
      });
  }

  #handleTagsCollapse(e :AgGridEvent) {
    this._viewSettings.updateState({ prefersTagsCollapsed: true });
  }
  #handleTagsExpand(e :AgGridEvent) {
    this._viewSettings.updateState({ prefersTagsCollapsed: false });
  }

  #listenForCustomGridEvents() {
    if (!this.gridApi) {
      console.warn('cannot bind listeners to custom grid events; gridApi not set');

      return;
    }

    const _listeners :[LumAgGridEvent, Function][] = [
      [
        LumAgGridEvent.COLUMN_TAGS_COLLAPSE,
        (e :AgGridEvent) => this.#handleTagsCollapse(e),
      ],
      [
        LumAgGridEvent.COLUMN_TAGS_EXPAND,
        (e :AgGridEvent) => this.#handleTagsExpand(e),
      ],
    ];

    _listeners.forEach((_l) => this.gridApi.addEventListener(..._l));

    this.gridApi.addEventListener('destroy', () =>
      _listeners.forEach((_l) => this.gridApi.removeEventListener(..._l)),
    );
  }

  private getApprovalStatus(value :string | null) :EApprovalStatus {
    const statusMap :{ [key :string] :EApprovalStatus } = {
      APPROVED: EApprovalStatus.APPROVED,
      REJECTED: EApprovalStatus.REJECTED,
      PARTIAL: EApprovalStatus.PARTIAL,
      PENDING: EApprovalStatus.PENDING,
    };

    return statusMap[value ?? ''] || EApprovalStatus.NONE;
  }

  private redirectAndCreatePicklist(salesOrderId :number) :void {
    if (this.ftPickAndPackEnabled) {
      this.createPicklist(false, salesOrderId);

      return;
    }

    this.dialog
      .open(UiConfirmationDialogComponent, {
        data: {
          title: 'Create picklist',
          message:
            'Do you want to create a picklist for these selected sales orders?',
          input: {
            type: 'checkbox',
            label: 'Automatically move inventory',
          },
        },
        width: DialogConfig.sizes.medium,
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result :{ dismiss :boolean; value ?:boolean } | boolean) => {
        if (typeof result === 'object' && !result.dismiss) {
          this.createPicklist(result.value || false, salesOrderId);
        }
      });
  }

  private createPicklist(updateInventory :boolean, salesOrderId :number) :void {
    this.loadingDialogService.show(
      'Creating Picklist for Sales Orders, please wait...',
    );

    this.picklistApiService
      .bulkCreatePicklist({
        salesOrders: [salesOrderId],
        fulfill: updateInventory,
      })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe({
        next: (response) => {
          this.loadingDialogService.close();

          if (response && response.error) {
            this.handlePicklistErrorResponse(response, updateInventory);

            return;
          }

          this.snackBar.open(
            response.success || 'Picklist successfully created',
            'Success',
            {
              duration: SnackBarConfig.DEFAULT.duration,
            },
          );

          if (this.ftPickAndPackEnabled) {
            if (response && response.id) {
              this.router.navigateByUrl(`/shipping/picklists/${response.id}`);
            } else {
              this.router.navigateByUrl('/shipping/picklists');
            }
          }
        },
        error: (response) => {
          this.loadingDialogService.close();

          this.snackBar.open(response.error, 'Fail', {
            duration: SnackBarConfig.DEFAULT.duration,
          });
        },
      });
  }

  private handlePicklistErrorResponse(
    response :{
      error :string;
      data :IOrderPickingPreviewDialogInterface;
    },
    updateInventory :boolean = false,
  ) :void {
    this.snackBar.open(response.error, 'Fail', {
      duration: SnackBarConfig.DEFAULT.duration,
    });

    if (!response.data) {
      return;
    }

    this.dialog
      .open(PicklistPreviewDialogComponent, {
        data: {
          pickingPreviewData: response.data,
          errors: [response.error],
        },
        width: DialogConfig.sizes['xx-large'],
      })
      .afterClosed()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((result) => {
        if (result) {
          this.createPicklist(updateInventory, result[0]); // expected to get one index due to indiviual picklist
        }
      });
  }

  #toggleSelectAllRows(isSelectAllRows :boolean) :void {
    this.isSelectAllRows = isSelectAllRows;

    // This causes the selected rows when the result is few: CS-4767
    // Comment for now for future debugging
    // this.selectedRows = [];

    this.consolidatedOrderItems = [];

    if (this._viewSettings.get('prefersSidebarOpen')) {
      if (this.isSelectAllRows) {
        this.#closeSidebar();
      } else {
        this.#openSidebar();
      }
    }

    this.agGridPaginationUtilService.notifySelectAllChange({
      pagination: this.pagination,
      selectedRows: this.selectedRows,
      isSelectAllRows: this.isSelectAllRows,
    });
    this.cd.detectChanges();
  }

  private onClickCellRendererEvent(data :any, propertyName :string = '') :void {
    if (propertyName === this.fields.ORDER_NUMBER) {
      this.onClickOrderNumberEvent.emit(data.id ?? 0);
    }
  }

  private initOnDemandParams() :void {
    if (this.activeColumns.length) {
      this.activeColumns.forEach((column :string) => {
        this.updateOnDemandParamFromColumn(column);
      });
    }
  }
  private updateOnDemandParamFromColumn(column :string) :void {
    const onDemandColumn = this.onDemandServerSideColumns[column] ?? null;
    const onDemandRelation = this.onDemandServerSideRelations[column] ?? null;
    const isCustomField = column?.startsWith('cf__');

    this.pushOnDemandColumnAndRelationForParams(
      onDemandColumn,
      onDemandRelation,
      isCustomField,
    );
  }
  private pushOnDemandColumnAndRelationForParams(
    onDemandColumn :string,
    onDemandRelation :string,
    isCustomField :boolean = false,
  ) :void {
    if (onDemandColumn && !this.columns.includes(onDemandColumn)) {
      this.columns.push(onDemandColumn);
    }

    if (onDemandRelation && !this.relations.includes(onDemandRelation)) {
      this.relations.push(onDemandRelation);
    }
    if (
      isCustomField &&
      !this.relations.includes(
        this.onDemandServerSideRelations[this.fields.CUSTOM_FIELDS],
      )
    ) {
      this.relations.push(
        this.onDemandServerSideRelations[this.fields.CUSTOM_FIELDS],
      );
    }
  }

  get selectedRowCount() :number {
    return this.selectedRows.length;
  }

  get warehouseIds() :number[] {
    return this.warehouseGroups.map((row :IWarehouseGroup) => row.id);
  }

  get appIntegrationAccountIds() :number[] {
    return this.appIntegrationAccounts.map((row :IAppIntegrationAccount) => row.id);
  }

  get channelIds() :number[] {
    return this.channels.map((row :IChannel) => row.id);
  }

  get activeChannelIds() :number[] {
    return this.channels.filter((row :IChannel) => row.status).map((row) => row.id);
  }

  get tagIds() :number[] {
    return this.tags.map((row :ITagOld) => row.id);
  }

  get salesOrderStorage() :any {
    return JSON.parse(
      localStorage.getItem(LocalStorageKeys.SALES_ORDER_FILTERS_SORTINGS) || '{}',
    );
  }

  get storedPageState() :any {
    const pageState = localStorage.getItem(LocalStorageKeys.SALES_ORDER_AG_STATE);

    return pageState ? JSON.parse(pageState) : null;
  }

  get currentActiveCustomFilters() :any {
    return this.storedPageState?.activeCustomFilters || null;
  }

  get currentFilterModel() :any {
    return this.gridApi?.getFilterModel();
  }

  get currentColumnState() :any {
    return this.gridColumnApi?.getColumnState();
  }

  get storedPagination() :any {
    return JSON.parse(
      localStorage.getItem(LocalStorageKeys.SALES_ORDER_TABLE_PAGINATION) || '{}',
    );
  }

  get currentGroupOption() :any {
    return this.groupBy
      ? this.groupByOptions.find(({ field }) => field === this.groupBy)
      : '';
  }

  get currentGroupsTotalCount() :number {
    return this.groups.reduce((acc, curr) => acc + curr.count, 0);
  }

  get getSelectedOrderNumbersDisplay() :string {
    return this.selectedRows.map((row) => row.orderNumber).join(', ');
  }

  get hasShippingLabel() :boolean {
    if (this.firstSelectedRow && this.shipstationShipments?.length) {
      const shipmentsWithLabelData = this.shipstationShipments.filter(
        (shipment :any) => shipment.labelData,
      );

      return shipmentsWithLabelData.length > 0;
    }

    return false;
  }

  get firstSelectedRow() :any {
    if (this.selectedRows.length === 1) {
      return this.selectedRows[0];
    }

    return null;
  }

  get singleSelectedRow() :any {
    if (this.selectedRows.length === 1) {
      const {
        shipTo,
        shipDate,
        id,
        carrierCode,
        serviceCode,
        packageCode,
        weight,
        dimensions,
        confirmation,
        orderStatus,
        salesOrderShipments,
        orderItems,
      } = this.selectedRows[0];

      let hasMultipleShipments = false;
      if (salesOrderShipments?.length > 1) {
        hasMultipleShipments = this.featureFlagUtilService.isFeatureEnabled(
          'red_DV-2840_partial_fulfillment',
        );
      } else if (salesOrderShipments?.length === 1) {
        const hasShippedItems =
          orderItems?.some(
            (item) => item.status !== ESalesOrderStatusValue.PENDING,
          ) || false;
        const hasUnshippedItems =
          orderItems?.some(
            (item) => item.status === ESalesOrderStatusValue.PENDING,
          ) || false;
        hasMultipleShipments =
          this.featureFlagUtilService.isFeatureEnabled(
            'red_DV-2840_partial_fulfillment',
          ) &&
          hasShippedItems &&
          hasUnshippedItems;
      }

      return {
        shipTo,
        shipDate,
        salesOrderId: id,
        carrierCode,
        serviceCode,
        packageCode,
        weight,
        dimensions,
        confirmation,
        isBulkUpdate: false,
        orderStatus,
        ...(hasMultipleShipments && {
          hasMultipleShipments: hasMultipleShipments,
          salesOrderShipments: salesOrderShipments,
          salesOrderShipmentId: null,
        }),
      };
    }

    return null;
  }

  get expectedRecordsCount() :number {
    return (this.pagination.current_page + 1) * this.pagination.per_page;
  }

  get goToPageValue() :number {
    if (this.pagination.pageIndex != null) {
      return this.pagination.pageIndex + 1;
    } else {
      return this.pagination.current_page || 1;
    }
  }

  get showLabelSelectAllRows() :boolean {
    return (
      !this.isSelectAllRows &&
      this.selectedRows.length !== 0 &&
      this.remainingSelectableCount !== 0
    );
  }

  get remainingSelectableCount() :number {
    const remaining = this.totalRowCount - this.selectedRows.length;

    return remaining < 0 ? remaining * -1 : remaining;
  }

  get selecatableRowData() :ISalesOrder[] {
    return this.rowData.filter((data :ISalesOrder) => data.isPosted);
  }

  get activeColumns() :string[] {
    const activeColumns :string[] = [];
    if (this.storedPageState?.columnState?.length) {
      this.storedPageState.columnState.map((column :any) => {
        if (!column.hide) {
          activeColumns.push(column.colId);
        }
      });
    } else if (this.columnDefs?.length) {
      this.columnDefs.map((column :any) => {
        if (!column.hide) {
          activeColumns.push(column.field);
        }
      });
    }

    return activeColumns;
  }

  onActionClick(action :IOrderActions) :void {
    const parentSelectedRows = this.lineItemGridApi?.getSelectedRows();
    const childSelectedRows = this.lineItemDetailGridApi?.getSelectedRows();

    if (
      parentSelectedRows?.length === 0 &&
      (!childSelectedRows || !childSelectedRows?.length)
    ) {
      this.snackBar.open(
        'No rows selected. Please select at least one row.',
        'Dismiss',
        { ...SnackBarConfig.ERROR },
      );

      return;
    }

    action.onRowClick({
      parentSelectedRows,
      childSelectedRows,
    });
  }

  onLineItemGridReady(event :GridReadyEvent) :void {
    this.lineItemGridApi = event.api;
  }

  onLineItemDetailGridReady(event :GridReadyEvent) :void {
    this.lineItemDetailGridApi = event.api;
  }
}
